/**
 * VlanPathCheck -- the "Check path" button and the hop-chain dialog (OP#1818, epic #1817).
 *
 * WHY: at Jedi Home (2026-10-12/13) an SSID on VLAN 1000 was on air and DHCP never answered; VLAN
 * 1000 was missing on a SonicFi switch behind the Edgecore switches and Meridian showed every
 * device green. This dialog answers "does VLAN N reach a gateway from this AP?" and NAMES the
 * first hop that is broken (proven) or cannot be checked, in one plain sentence first.
 *
 * Layout: a horizontal chain read gateway-first -- "gateway <- switch:port <- switch:port <- AP <-
 * SSID" -- one chain per AP. Each node carries the status ramp (healthy / warn / critical / info)
 * through StatusPill; brand violet is used only by action buttons. Technical details (expected vs
 * actual, evidence and its age, the stable code) sit in a collapsed <details>. Tablet-first: the
 * chain scrolls horizontally INSIDE its own row, so a narrow screen never scrolls the page.
 *
 * Read-only: the server uses stored reads only and never opens a device session. A hop that a
 * fresh switch read would settle names that action ("Read VLANs from switch X"); this dialog
 * does not trigger it.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { ErrorBanner } from "@/components/ui/Page";
import { useVlanPath } from "@/lib/api/vlanPath";
import {
  chainForAp,
  hopDetails,
  pathLabel,
  pathTone,
  rolloutPathLines,
  type ChainNode,
  type PathApResult,
  type PathHop,
  type RolloutPathCheck,
  type VlanPathCompact,
} from "@/lib/vlanPath";
import { cn } from "@/lib/cn";

/** Border accent per node status (the ramp tokens, both themes). */
const NODE_BORDER: Record<string, string> = {
  ok: "border-healthy/50",
  broken: "border-critical/70",
  unknown: "border-warn/70",
  na: "border-line",
};

/** "Check path" -- opens the dialog for one VLAN or one SSID. Any role that sees the site. */
export function VlanPathButton({
  siteId,
  vlan,
  ssid,
  label = "Check path",
  variant = "secondary",
}: {
  siteId: string;
  vlan?: number | null;
  ssid?: string | null;
  label?: string;
  variant?: "secondary" | "ghost";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant={variant}
        onClick={() => setOpen(true)}
        title="Trace this VLAN from every AP to the gateway (read-only)"
        data-testid="vlan-path-button"
      >
        {label}
      </Button>
      {open && <VlanPathDialog siteId={siteId} vlan={vlan} ssid={ssid} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The dialog: sentence first, then one chain per AP, details collapsed. */
export function VlanPathDialog({
  siteId,
  vlan,
  ssid,
  onClose,
}: {
  siteId: string;
  vlan?: number | null;
  ssid?: string | null;
  onClose: () => void;
}) {
  const q = useVlanPath(siteId, { vlan, ssid }, true);
  const r = q.data;
  const what = ssid ? `SSID ${ssid}` : `VLAN ${vlan}`;
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          className="md-dialog fixed left-1/2 top-1/2 z-sheet flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="text-lg font-semibold text-ink">VLAN path · {what}</Dialog.Title>
            {r && (
              <StatusPill status={pathTone(r.status)} title={r.status}>
                {pathLabel(r.status)}
              </StatusPill>
            )}
          </div>
          <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
            {q.isLoading && <p className="text-sm text-ink-muted">Tracing {what} from every AP…</p>}
            {q.error != null && <ErrorBanner error={q.error} />}
            {r && (
              <div className="flex flex-col gap-4" data-testid="vlan-path-result">
                {/* The plain-language answer, first. */}
                <p className="text-sm font-medium text-ink" data-testid="vlan-path-summary">
                  {r.summary}
                </p>
                {r.aps.map((ap) => (
                  <ApPath key={ap.ap.serial ?? ap.ap.name} ap={ap} ssid={r.ssid} />
                ))}
                {r.aps.length === 0 && r.gateways.length > 0 && (
                  <HopDetails hops={r.gateways} title="Gateway checks" />
                )}
                {r.not_carrying.length > 0 && (
                  <p className="text-xs text-ink-subtle">
                    Not carrying it: {r.not_carrying.map((d) => d.name ?? d.serial).join(", ")}
                  </p>
                )}
                <p className="text-xs text-ink-subtle">
                  {r.sources_note ?? "Stored reads only: no device was contacted."}
                </p>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => void q.refetch()} disabled={q.isFetching}>
              {q.isFetching ? "Checking…" : "Check again"}
            </Button>
            <Dialog.Close asChild>
              <Button variant="ghost">Close</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** One AP: its sentence, its chain, what would resolve the first unknown hop, the details. */
function ApPath({ ap, ssid }: { ap: PathApResult; ssid: string | null }) {
  const chain = chainForAp(ap, ssid);
  const action = (ap.first_broken ?? ap.first_unknown)?.action;
  return (
    <section className="md-card p-3" data-testid="vlan-path-ap" aria-label={`Path from ${ap.ap.name}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-ink">{ap.ap.name}</span>
        <StatusPill status={pathTone(ap.status)}>{pathLabel(ap.status)}</StatusPill>
      </div>
      <p className="mt-1 text-sm text-ink-muted">{ap.summary}</p>
      <Chain nodes={chain} />
      {action && (
        <p className="mt-1 text-xs text-ink-muted">
          To resolve: <span className="font-medium text-ink">{action.label}</span> (Ports page; an
          organisation administrator can read the switch).
        </p>
      )}
      <HopDetails hops={ap.hops} title="Technical details" />
    </section>
  );
}

/** The horizontal chain. Scrolls inside its own row on a narrow screen. */
function Chain({ nodes }: { nodes: ChainNode[] }) {
  return (
    <ol
      className="mt-2 flex items-stretch gap-1 overflow-x-auto pb-2"
      aria-label="Path from the gateway to the SSID"
      data-testid="vlan-path-chain"
    >
      {nodes.map((n, i) => (
        <li key={n.key} className="flex shrink-0 items-center gap-1">
          {i > 0 && (
            <span aria-hidden className="px-0.5 text-ink-subtle">
              ←
            </span>
          )}
          <div
            className={cn(
              "flex min-w-[7rem] flex-col gap-1 rounded border bg-surface px-2 py-1.5",
              NODE_BORDER[n.status] ?? "border-line",
            )}
            data-status={n.status}
            data-testid="vlan-path-node"
          >
            <span className="truncate text-xs font-medium text-ink" title={n.title}>
              {n.title}
            </span>
            {n.sub && <span className="font-mono text-[0.6875rem] text-ink-muted">{n.sub}</span>}
            {n.kind !== "ap" && (
              <StatusPill status={pathTone(n.status)} className="self-start px-1.5 py-0.5 text-[0.625rem]">
                {n.status === "ok" ? "OK" : n.status === "broken" ? "Broken" : n.status === "unknown" ? "Unknown" : "n/a"}
              </StatusPill>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Collapsed technical rows: expected vs actual, evidence + age, reason, code. */
function HopDetails({ hops, title }: { hops: PathHop[]; title: string }) {
  return (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer select-none text-ink-muted">{title}</summary>
      <ul className="mt-2 flex flex-col gap-2">
        {hops.map((h, i) => (
          <li key={`${h.hop}-${h.device.serial}-${h.port}-${i}`} className="rounded border border-line p-2">
            <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-2 gap-y-0.5">
              {hopDetails(h).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-ink-subtle">{k}</dt>
                  <dd className={cn("break-words text-ink", k === "Code" && "font-mono")}>{v}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** The post-rollout path lines (rollout.verification.path_check), worst first. */
export function RolloutPathLines({ pc }: { pc: RolloutPathCheck | null | undefined }) {
  const lines = rolloutPathLines(pc);
  if (!lines.length) return null;
  return (
    <ul className="border-b border-line px-3 py-2 text-sm" data-testid="rollout-path-lines">
      {lines.map((l) => (
        <li key={l.text} className={l.tone === "critical" ? "text-critical" : "text-warn"}>
          {l.text}
        </li>
      ))}
    </ul>
  );
}

/** A compact result (SSID dry run): the sentence with its pill. */
export function PathCompactLine({ pc }: { pc: VlanPathCompact | null | undefined }) {
  if (!pc || pc.status === "ok" || pc.status === "na") return null;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink">
      <StatusPill status={pathTone(pc.status)}>{pathLabel(pc.status)}</StatusPill>
      <span>{pc.summary}</span>
    </p>
  );
}
